import React, { useState, useEffect, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { supabase } from '../../lib/supabase';

const PAGE_URL_MAP = {
     home: '/',
     'about-us': '/about-us',
     faq: '/faq',
     pricing: '/pricing',
     schedule: '/schedule',
     safety: '/safety',
};

export default function Heatmap() {
     const [pages, setPages] = useState([]);
     const [selectedPage, setSelectedPage] = useState('');
     const [fromDate, setFromDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
     const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
     const [device, setDevice] = useState('all');
     const [view, setView] = useState('heatmap');
     const [clicks, setClicks] = useState([]);
     const [scrollBuckets, setScrollBuckets] = useState(Array(10).fill(0));
     const [totalSessions, setTotalSessions] = useState(0);
     const [loading, setLoading] = useState(false);
     const [analytics, setAnalytics] = useState(null);

     const canvasRef = useRef(null);
     const viewportRef = useRef(null);
     const iframeRef = useRef(null);

     // Load pages with click counts
     useEffect(() => {
          loadPages();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

     async function loadPages() {
          const { data } = await supabase
               .from('heatmap_clicks')
               .select('page');

          if (!data) return;

          const counts = {};
          data.forEach((row) => {
               counts[row.page] = (counts[row.page] || 0) + 1;
          });

          const pageList = Object.entries(counts)
               .map(([page, count]) => ({ page, count }))
               .sort((a, b) => b.count - a.count);

          setPages(pageList);
          if (pageList.length > 0) {
               setSelectedPage(pageList[0].page);
               loadData(pageList[0].page);
          }
     }

     // Load data — accepts optional page override for initial load
     async function loadData(pageOverride) {
          const page = pageOverride || selectedPage;
          if (!page) return;
          setLoading(true);

          let clickQuery = supabase
               .from('heatmap_clicks')
               .select('*')
               .eq('page', page)
               .gte('created_at', fromDate)
               .lt('created_at', toDate + 'T23:59:59')
               .order('created_at', { ascending: false })
               .limit(5000);

          let scrollQuery = supabase
               .from('heatmap_scroll')
               .select('*')
               .eq('page', page)
               .gte('created_at', fromDate)
               .lt('created_at', toDate + 'T23:59:59');

          if (device !== 'all') {
               clickQuery = clickQuery.eq('device', device);
               scrollQuery = scrollQuery.eq('device', device);
          }

          const [clickRes, scrollRes] = await Promise.all([clickQuery, scrollQuery]);

          setClicks(clickRes.data || []);

          // Build scroll buckets (0-10%, 10-20%, ..., 90-100%)
          const buckets = Array(10).fill(0);
          const scrollData = scrollRes.data || [];
          scrollData.forEach((s) => {
               const bucket = Math.min(Math.floor(s.max_depth / 10), 9);
               buckets[bucket]++;
          });
          setScrollBuckets(buckets);
          setTotalSessions(scrollData.length);
          setLoading(false);
     }

     // Load analytics data
     async function loadAnalytics() {
          const endDate = toDate + 'T23:59:59';

          let query = supabase
               .from('page_views')
               .select('*')
               .gte('created_at', fromDate)
               .lt('created_at', endDate)
               .order('created_at', { ascending: false })
               .limit(10000);

          if (selectedPage) query = query.eq('page', selectedPage);
          if (device !== 'all') query = query.eq('device', device);

          const { data } = await query;
          const views = data || [];

          // Unique visitors
          const uniqueVisitors = new Set(views.map(v => v.visitor_id)).size;
          const uniqueSessions = new Set(views.map(v => v.session_id)).size;

          // Pages per session
          const sessionPages = {};
          views.forEach(v => {
               sessionPages[v.session_id] = (sessionPages[v.session_id] || 0) + 1;
          });
          const avgPagesPerSession = uniqueSessions > 0
               ? (views.length / uniqueSessions).toFixed(1)
               : '0';

          // Bounce rate (sessions with only 1 page view)
          const bounceSessions = Object.values(sessionPages).filter(c => c === 1).length;
          const bounceRate = uniqueSessions > 0
               ? Math.round((bounceSessions / uniqueSessions) * 100)
               : 0;

          // Page views by day
          const byDay = {};
          views.forEach(v => {
               const day = v.created_at.substring(0, 10);
               byDay[day] = (byDay[day] || 0) + 1;
          });
          const dailyViews = Object.entries(byDay).sort((a, b) => a[0].localeCompare(b[0]));

          // Top referrers
          const refCounts = {};
          views.forEach(v => {
               if (v.referrer) {
                    refCounts[v.referrer] = (refCounts[v.referrer] || 0) + 1;
               }
          });
          const topReferrers = Object.entries(refCounts).sort((a, b) => b[1] - a[1]).slice(0, 10);

          // Browser breakdown
          const browserCounts = {};
          views.forEach(v => {
               if (v.browser) browserCounts[v.browser] = (browserCounts[v.browser] || 0) + 1;
          });
          const browsers = Object.entries(browserCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

          // OS breakdown
          const osCounts = {};
          views.forEach(v => {
               if (v.os) osCounts[v.os] = (osCounts[v.os] || 0) + 1;
          });
          const osList = Object.entries(osCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

          // Device breakdown
          const deviceCounts = {};
          views.forEach(v => {
               if (v.device) deviceCounts[v.device] = (deviceCounts[v.device] || 0) + 1;
          });
          const devices = Object.entries(deviceCounts).sort((a, b) => b[1] - a[1]);

          // Location breakdown
          const locationCounts = {};
          views.forEach(v => {
               const loc = [v.city, v.region, v.country].filter(Boolean).join(', ');
               if (loc) locationCounts[loc] = (locationCounts[loc] || 0) + 1;
          });
          const locations = Object.entries(locationCounts).sort((a, b) => b[1] - a[1]).slice(0, 15);

          // UTM sources
          const utmCounts = {};
          views.forEach(v => {
               const src = v.utm_source;
               if (src) utmCounts[src] = (utmCounts[src] || 0) + 1;
          });
          const utmSources = Object.entries(utmCounts).sort((a, b) => b[1] - a[1]).slice(0, 10);

          setAnalytics({
               totalViews: views.length,
               uniqueVisitors,
               uniqueSessions,
               avgPagesPerSession,
               bounceRate,
               dailyViews,
               topReferrers,
               browsers,
               osList,
               devices,
               locations,
               utmSources,
          });
     }

     // Reload when filters change (skip initial load, handled by loadPages)
     const initialLoadDone = useRef(false);
     useEffect(() => {
          if (!initialLoadDone.current) {
               initialLoadDone.current = true;
               return;
          }
          if (selectedPage) loadData();
          if (view === 'analytics') loadAnalytics();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [selectedPage, fromDate, toDate, device]);

     // Render heatmap on canvas
     useEffect(() => {
          if (view !== 'heatmap' || clicks.length === 0 || !canvasRef.current || !viewportRef.current) return;

          const canvas = canvasRef.current;
          const viewport = viewportRef.current;
          const iframe = iframeRef.current;

          let maxDW = 0, maxDH = 0;
          clicks.forEach((c) => {
               if (c.dw > maxDW) maxDW = c.dw;
               if (c.dh > maxDH) maxDH = c.dh;
          });

          const origWidth = maxDW || 1400;
          const origHeight = maxDH || 3000;

          const containerWidth = viewport.parentElement.offsetWidth;
          const scale = containerWidth / origWidth;
          const displayWidth = containerWidth;
          const displayHeight = origHeight * scale;

          viewport.style.width = displayWidth + 'px';
          viewport.style.height = displayHeight + 'px';

          canvas.width = displayWidth;
          canvas.height = displayHeight;

          function drawHeatmap() {
               const ctx = canvas.getContext('2d', { willReadFrequently: true });
               ctx.clearRect(0, 0, displayWidth, displayHeight);

               const radius = Math.max(20, Math.min(40, displayWidth / 30));
               clicks.forEach((c) => {
                    const x = c.x * scale;
                    const y = c.y * scale;
                    const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
                    grad.addColorStop(0, 'rgba(255, 0, 0, 0.15)');
                    grad.addColorStop(1, 'rgba(255, 0, 0, 0)');
                    ctx.fillStyle = grad;
                    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
               });

               // Color mapping
               const imageData = ctx.getImageData(0, 0, displayWidth, displayHeight);
               const data = imageData.data;
               for (let i = 0; i < data.length; i += 4) {
                    const alpha = data[i + 3];
                    if (alpha === 0) continue;
                    const intensity = Math.min(alpha / 200, 1);
                    let r, g, b;
                    if (intensity < 0.25) {
                         r = 0; g = Math.round(intensity * 4 * 255); b = 255;
                    } else if (intensity < 0.5) {
                         r = 0; g = 255; b = Math.round((1 - (intensity - 0.25) * 4) * 255);
                    } else if (intensity < 0.75) {
                         r = Math.round((intensity - 0.5) * 4 * 255); g = 255; b = 0;
                    } else {
                         r = 255; g = Math.round((1 - (intensity - 0.75) * 4) * 255); b = 0;
                    }
                    data[i] = r;
                    data[i + 1] = g;
                    data[i + 2] = b;
                    data[i + 3] = Math.min(Math.round(intensity * 180 + 40), 220);
               }
               ctx.putImageData(imageData, 0, 0);
          }

          // Load page in iframe, draw heatmap after it loads
          if (iframe) {
               const pageUrl = PAGE_URL_MAP[selectedPage] || '/' + selectedPage;
               iframe.style.width = origWidth + 'px';
               iframe.style.height = origHeight + 'px';
               iframe.style.transform = `scale(${scale})`;
               iframe.style.transformOrigin = 'top left';
               iframe.onload = drawHeatmap;
               iframe.src = pageUrl;
          } else {
               drawHeatmap();
          }
     }, [view, clicks, selectedPage]);

     // Stats
     const avgScroll = (() => {
          const total = scrollBuckets.reduce((sum, count, i) => sum + count * (i * 10 + 5), 0);
          const sessions = scrollBuckets.reduce((s, c) => s + c, 0);
          return sessions > 0 ? Math.round(total / sessions) : 0;
     })();

     const topElements = (() => {
          const counts = {};
          clicks.forEach((c) => {
               const key = c.el_text || c.el_id || c.el;
               if (key) counts[key] = (counts[key] || 0) + 1;
          });
          return Object.entries(counts)
               .sort((a, b) => b[1] - a[1])
               .slice(0, 15);
     })();

     const topElement = topElements.length > 0 ? topElements[0][0] : '-';
     const topMax = topElements.length > 0 ? topElements[0][1] : 1;

     const inputClasses = 'bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-darker text-sm focus:outline-none focus:border-yellow duration-300';

     return (
          <div>
               <h1 className='text-darker text-2xl font-bold mb-6'>Heatmap</h1>

               {/* Controls */}
               <div className='bg-white rounded-xl p-4 border border-gray-200 mb-6 flex flex-wrap gap-4 items-end'>
                    <div>
                         <label className='block text-darker/40 text-xs mb-1'>Page</label>
                         <select
                              value={selectedPage}
                              onChange={(e) => setSelectedPage(e.target.value)}
                              className={inputClasses}
                         >
                              {pages.length === 0 && <option value=''>No data yet</option>}
                              {pages.map((p) => (
                                   <option key={p.page} value={p.page}>
                                        {p.page} ({p.count} clicks)
                                   </option>
                              ))}
                         </select>
                    </div>
                    <div>
                         <label className='block text-darker/40 text-xs mb-1'>From</label>
                         <input
                              type='date'
                              value={fromDate}
                              onChange={(e) => setFromDate(e.target.value)}
                              className={inputClasses}
                         />
                    </div>
                    <div>
                         <label className='block text-darker/40 text-xs mb-1'>To</label>
                         <input
                              type='date'
                              value={toDate}
                              onChange={(e) => setToDate(e.target.value)}
                              className={inputClasses}
                         />
                    </div>
                    <div>
                         <label className='block text-darker/40 text-xs mb-1'>Device</label>
                         <select
                              value={device}
                              onChange={(e) => setDevice(e.target.value)}
                              className={inputClasses}
                         >
                              <option value='all'>All</option>
                              <option value='desktop'>Desktop</option>
                              <option value='tablet'>Tablet</option>
                              <option value='mobile'>Mobile</option>
                         </select>
                    </div>
                    <div>
                         <label className='block text-darker/40 text-xs mb-1'>View</label>
                         <div className='flex gap-1'>
                              {['heatmap', 'clicks', 'scroll', 'analytics'].map((v) => (
                                   <button
                                        key={v}
                                        onClick={() => { setView(v); if (v === 'analytics' && !analytics) loadAnalytics(); }}
                                        className={`px-3 py-1.5 rounded-lg text-sm border duration-300 capitalize ${
                                             view === v
                                                  ? 'border-yellow text-yellow bg-yellow/10'
                                                  : 'border-gray-200 text-darker/40 hover:text-darker'
                                        }`}
                                   >
                                        {v}
                                   </button>
                              ))}
                         </div>
                    </div>
                    {selectedPage && (
                         <button
                              onClick={async () => {
                                   if (!window.confirm(`Clear ALL heatmap data for "${selectedPage}"? This cannot be undone.`)) return;
                                   await Promise.all([
                                        supabase.from('heatmap_clicks').delete().eq('page', selectedPage),
                                        supabase.from('heatmap_scroll').delete().eq('page', selectedPage),
                                        supabase.from('page_views').delete().eq('page', selectedPage),
                                   ]);
                                   loadData();
                                   if (view === 'analytics') loadAnalytics();
                              }}
                              className='px-3 py-1.5 rounded-lg text-sm border border-red-500/30 text-red-400 hover:bg-red-500/10 duration-300'
                         >
                              Clear Data
                         </button>
                    )}
               </div>

               {!loading && clicks.length === 0 && totalSessions === 0 && view !== 'analytics' && (
                    <div className='bg-white rounded-xl p-8 border border-gray-200 text-center'>
                         <p className='text-darker/40 text-lg mb-2'>No Heatmap Data Yet</p>
                         <p className='text-darker/30 text-sm'>
                              The tracker is installed on your site. As visitors interact with your pages,
                              click data will appear here.
                         </p>
                    </div>
               )}

               {(clicks.length > 0 || totalSessions > 0 || view === 'analytics') && (
                    <>
                         {/* Stats */}
                         <div className='grid grid-cols-2 tablet:grid-cols-4 gap-4 mb-6'>
                              <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                   <p className='text-darker text-2xl font-bold'>{clicks.length.toLocaleString()}</p>
                                   <p className='text-darker/40 text-sm'>Total Clicks</p>
                              </div>
                              <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                   <p className='text-darker text-2xl font-bold'>{totalSessions.toLocaleString()}</p>
                                   <p className='text-darker/40 text-sm'>Sessions</p>
                              </div>
                              <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                   <p className='text-darker text-2xl font-bold'>{avgScroll}%</p>
                                   <p className='text-darker/40 text-sm'>Avg Scroll Depth</p>
                              </div>
                              <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                   <p className='text-yellow text-sm font-bold truncate' title={topElement}>
                                        "{topElement.substring(0, 20)}"
                                   </p>
                                   <p className='text-darker/40 text-sm'>Most Clicked</p>
                              </div>
                         </div>

                         {/* Heatmap View */}
                         {view === 'heatmap' && (
                              <div className='flex flex-col laptop:flex-row gap-6'>
                                   <div className='flex-1'>
                                        <div
                                             ref={viewportRef}
                                             className='relative overflow-hidden rounded-xl border border-gray-200 bg-white'
                                        >
                                             {loading && (
                                                  <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/80'>
                                                       <span className='text-black/40 text-sm'>Loading heatmap data...</span>
                                                  </div>
                                             )}
                                             <iframe
                                                  ref={iframeRef}
                                                  title='Heatmap Preview'
                                                  className='absolute top-0 left-0 border-0'
                                                  scrolling='no'
                                                  style={{ pointerEvents: 'none' }}
                                             />
                                             <canvas
                                                  ref={canvasRef}
                                                  className='absolute top-0 left-0'
                                                  style={{ pointerEvents: 'none' }}
                                             />
                                        </div>
                                   </div>
                                   <div className='laptop:w-72'>
                                        <div className='bg-white rounded-xl p-4 border border-gray-200'>
                                             <h3 className='text-darker text-sm font-bold mb-3'>Most Clicked Elements</h3>
                                             {topElements.length === 0 ? (
                                                  <p className='text-darker/30 text-sm'>No data</p>
                                             ) : (
                                                  <div className='space-y-2'>
                                                       {topElements.map(([label, count]) => (
                                                            <div key={label} className='relative'>
                                                                 <div
                                                                      className='absolute top-0 left-0 h-full bg-yellow/10 rounded'
                                                                      style={{ width: `${(count / topMax) * 100}%` }}
                                                                 />
                                                                 <div className='relative flex justify-between px-2 py-1 text-xs'>
                                                                      <span className='text-darker/60 truncate mr-2' title={label}>
                                                                           {label.substring(0, 25)}
                                                                      </span>
                                                                      <span className='text-yellow font-bold'>{count}</span>
                                                                 </div>
                                                            </div>
                                                       ))}
                                                  </div>
                                             )}
                                        </div>
                                   </div>
                              </div>
                         )}

                         {/* Click List View */}
                         {view === 'clicks' && (
                              <div className='bg-white rounded-xl border border-gray-200 overflow-hidden'>
                                   <div className='p-4 border-b border-gray-200'>
                                        <h3 className='text-darker text-sm font-bold'>Click Log</h3>
                                   </div>
                                   <div className='overflow-x-auto'>
                                        <table className='w-full text-left text-sm'>
                                             <thead>
                                                  <tr className='border-b border-gray-200 text-darker/40'>
                                                       <th className='p-3'>Element</th>
                                                       <th className='p-3'>ID / Text</th>
                                                       <th className='p-3'>Position</th>
                                                       <th className='p-3'>Device</th>
                                                       <th className='p-3'>Time</th>
                                                  </tr>
                                             </thead>
                                             <tbody className='divide-y divide-gray-200'>
                                                  {clicks.slice(0, 200).map((c) => (
                                                       <tr key={c.id} className='hover:bg-gray-100'>
                                                            <td className='p-3 text-darker/60 font-mono text-xs'>
                                                                 &lt;{c.el || '?'}&gt;
                                                            </td>
                                                            <td className='p-3 text-darker truncate max-w-[200px]'>
                                                                 {c.el_id ? `#${c.el_id}` : c.el_text?.substring(0, 40) || '-'}
                                                            </td>
                                                            <td className='p-3 text-darker/40 whitespace-nowrap'>
                                                                 {c.x}, {c.y}
                                                            </td>
                                                            <td className='p-3'>
                                                                 <span className='px-2 py-0.5 rounded text-xs bg-gray-50 text-darker/60'>
                                                                      {c.device}
                                                                 </span>
                                                            </td>
                                                            <td className='p-3 text-darker/40 whitespace-nowrap text-xs'>
                                                                 {format(new Date(c.created_at), 'MMM d, h:mm a')}
                                                            </td>
                                                       </tr>
                                                  ))}
                                             </tbody>
                                        </table>
                                        {clicks.length === 0 && (
                                             <p className='text-darker/30 text-center py-8'>No clicks recorded</p>
                                        )}
                                   </div>
                              </div>
                         )}

                         {/* Scroll Depth View */}
                         {view === 'scroll' && (
                              <div className='flex flex-col tablet:flex-row gap-6'>
                                   <div className='flex-1 bg-white rounded-xl p-5 border border-gray-200'>
                                        <h3 className='text-darker text-sm font-bold mb-4'>Scroll Depth Distribution</h3>
                                        <div className='space-y-2'>
                                             {scrollBuckets.map((count, i) => {
                                                  const maxCount = Math.max(...scrollBuckets, 1);
                                                  const pct = (count / maxCount) * 100;
                                                  const label = `${i * 10}-${(i + 1) * 10}%`;
                                                  const colors = [
                                                       'bg-green-500', 'bg-green-400', 'bg-emerald-400', 'bg-emerald-300',
                                                       'bg-lime-400', 'bg-yellow', 'bg-amber-400', 'bg-orange-400',
                                                       'bg-red-400', 'bg-red-500'
                                                  ];
                                                  return (
                                                       <div key={i} className='flex items-center gap-3'>
                                                            <span className='text-darker/40 text-xs w-16 text-right'>{label}</span>
                                                            <div className='flex-1 bg-gray-50 rounded-full h-5 overflow-hidden'>
                                                                 <div
                                                                      className={`h-full rounded-full ${colors[i]} transition-all duration-500`}
                                                                      style={{ width: `${pct}%` }}
                                                                 />
                                                            </div>
                                                            <span className='text-darker text-xs w-8'>{count}</span>
                                                       </div>
                                                  );
                                             })}
                                        </div>
                                   </div>
                                   <div className='tablet:w-72 bg-white rounded-xl p-5 border border-gray-200'>
                                        <h3 className='text-darker text-sm font-bold mb-4'>Scroll Depth Breakdown</h3>
                                        <div className='space-y-2'>
                                             {scrollBuckets.map((count, i) => {
                                                  const total = scrollBuckets.reduce((s, c) => s + c, 0) || 1;
                                                  const pct = ((count / total) * 100).toFixed(1);
                                                  return (
                                                       <div key={i} className='flex justify-between text-sm'>
                                                            <span className='text-darker/40'>{i * 10}-{(i + 1) * 10}%</span>
                                                            <span className='text-darker font-bold'>{pct}%</span>
                                                       </div>
                                                  );
                                             })}
                                        </div>
                                   </div>
                              </div>
                         )}

                         {/* Analytics View */}
                         {view === 'analytics' && (
                              <div>
                                   {!analytics ? (
                                        <div className='text-darker/40 text-center py-12'>Loading analytics...</div>
                                   ) : analytics.totalViews === 0 ? (
                                        <div className='bg-white rounded-xl p-8 border border-gray-200 text-center'>
                                             <p className='text-darker/40 text-lg mb-2'>No Analytics Data Yet</p>
                                             <p className='text-darker/30 text-sm'>Page view tracking is active. Data will appear as visitors browse your site.</p>
                                        </div>
                                   ) : (
                                        <>
                                             {/* Analytics Stats */}
                                             <div className='grid grid-cols-2 tablet:grid-cols-4 gap-4 mb-6'>
                                                  <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                                       <p className='text-darker text-2xl font-bold'>{analytics.totalViews.toLocaleString()}</p>
                                                       <p className='text-darker/40 text-sm'>Page Views</p>
                                                  </div>
                                                  <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                                       <p className='text-darker text-2xl font-bold'>{analytics.uniqueVisitors.toLocaleString()}</p>
                                                       <p className='text-darker/40 text-sm'>Unique Visitors</p>
                                                  </div>
                                                  <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                                       <p className='text-darker text-2xl font-bold'>{analytics.avgPagesPerSession}</p>
                                                       <p className='text-darker/40 text-sm'>Pages / Session</p>
                                                  </div>
                                                  <div className='bg-white rounded-xl p-4 border border-gray-200 text-center'>
                                                       <p className='text-darker text-2xl font-bold'>{analytics.bounceRate}%</p>
                                                       <p className='text-darker/40 text-sm'>Bounce Rate</p>
                                                  </div>
                                             </div>

                                             {/* Daily Views Chart */}
                                             {analytics.dailyViews.length > 0 && (
                                                  <div className='bg-white rounded-xl p-5 border border-gray-200 mb-6'>
                                                       <h3 className='text-darker text-sm font-bold mb-4'>Page Views Over Time</h3>
                                                       <div className='flex items-end gap-1 h-40'>
                                                            {(() => {
                                                                 const maxDay = Math.max(...analytics.dailyViews.map(d => d[1]), 1);
                                                                 return analytics.dailyViews.map(([day, count]) => (
                                                                      <div key={day} className='flex-1 flex flex-col items-center justify-end h-full group'>
                                                                           <span className='text-yellow text-xs font-bold opacity-0 group-hover:opacity-100 mb-1'>{count}</span>
                                                                           <div
                                                                                className='w-full bg-yellow/70 rounded-t hover:bg-yellow transition-colors min-h-[2px]'
                                                                                style={{ height: `${(count / maxDay) * 100}%` }}
                                                                                title={`${format(new Date(day + 'T12:00:00'), 'MMM d')}: ${count} views`}
                                                                           />
                                                                           {analytics.dailyViews.length <= 14 && (
                                                                                <span className='text-darker/30 text-[10px] mt-1'>{format(new Date(day + 'T12:00:00'), 'M/d')}</span>
                                                                           )}
                                                                      </div>
                                                                 ));
                                                            })()}
                                                       </div>
                                                  </div>
                                             )}

                                             <div className='grid grid-cols-1 tablet:grid-cols-2 gap-6 mb-6'>
                                                  {/* Locations */}
                                                  <AnalyticsPanel title='Visitor Locations' items={analytics.locations} />

                                                  {/* Referrers */}
                                                  <AnalyticsPanel title='Top Referrers' items={analytics.topReferrers} emptyText='No referrer data (direct visits)' />

                                                  {/* Browsers */}
                                                  <AnalyticsPanel title='Browsers' items={analytics.browsers} />

                                                  {/* OS */}
                                                  <AnalyticsPanel title='Operating Systems' items={analytics.osList} />

                                                  {/* Devices */}
                                                  <AnalyticsPanel title='Devices' items={analytics.devices} />

                                                  {/* UTM Sources */}
                                                  {analytics.utmSources.length > 0 && (
                                                       <AnalyticsPanel title='Campaign Sources' items={analytics.utmSources} />
                                                  )}
                                             </div>
                                        </>
                                   )}
                              </div>
                         )}
                    </>
               )}
          </div>
     );
}

function AnalyticsPanel({ title, items, emptyText }) {
     const max = items.length > 0 ? items[0][1] : 1;
     return (
          <div className='bg-white rounded-xl p-4 border border-gray-200'>
               <h3 className='text-darker text-sm font-bold mb-3'>{title}</h3>
               {items.length === 0 ? (
                    <p className='text-darker/30 text-sm'>{emptyText || 'No data'}</p>
               ) : (
                    <div className='space-y-2'>
                         {items.map(([label, count]) => (
                              <div key={label} className='relative'>
                                   <div
                                        className='absolute top-0 left-0 h-full bg-yellow/10 rounded'
                                        style={{ width: `${(count / max) * 100}%` }}
                                   />
                                   <div className='relative flex justify-between px-2 py-1 text-xs'>
                                        <span className='text-darker/60 truncate mr-2' title={label}>
                                             {label.length > 35 ? label.substring(0, 35) + '...' : label}
                                        </span>
                                        <span className='text-yellow font-bold'>{count}</span>
                                   </div>
                              </div>
                         ))}
                    </div>
               )}
          </div>
     );
}
